// frontend/src/pages/AnalysisPage/sections/ReportGeneration/ReportGeneration.js
// Purpose: Manage report generation with configurable sections and export formats
// Desired outcome: Allow users to create and download executive summary PDFs and detailed Excel reports with proper data fetching and error handling

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFile,
  selectCalculationResults,
  selectValueSavingsData,
  selectReportData,
  updateReportConfig,
  updateReportName,
  generateReport,
  exportReportPdf,
  exportReportExcel,
  selectCalculations,
  getCalculationResults,
  getValueSavingsAnalysis,
  setStep
} from '../../../../state/analysis/analysisSlice';
import './ReportGeneration.css';

const ReportGeneration = () => {
  const dispatch = useDispatch();
  const currentFile = useSelector(selectCurrentFile);
  const calculationResults = useSelector(selectCalculationResults);
  const calculations = useSelector(selectCalculations);
  const valueSavingsData = useSelector(selectValueSavingsData);
  const reportData = useSelector(selectReportData);
  
  // Local state for report configuration
  const [reportConfig, setReportConfig] = useState({
    includeTopParts: true,
    includeTopCategories: true,
    includeRecommendations: true,
    includeExecutiveSummary: true,
    includeSavingsBreakdown: true
  });
  
  // Local state for report name
  const [reportName, setReportName] = useState(`Procurement Analysis - ${new Date().toLocaleDateString()}`);
  
  // Initialize config from Redux if available
  useEffect(() => {
    if (reportData?.reportConfig) {
      setReportConfig(reportData.reportConfig);
    }
    
    if (reportData?.name) {
      setReportName(reportData.name);
    }
  }, [reportData]);
  
  // Fetch calculation and value savings data if not available
  useEffect(() => {
    if (currentFile?.fileID) {
      // Only fetch if we don't have the data already
      if (!calculationResults || !calculationResults.parts || calculationResults.parts.length === 0) {
        dispatch(getCalculationResults(currentFile.fileID));
      }
      
      // Only fetch if we don't have the data already
      if (!valueSavingsData || !valueSavingsData.summary) {
        dispatch(getValueSavingsAnalysis(currentFile.fileID));
      }
    }
  }, [dispatch, currentFile, calculationResults, valueSavingsData]);
  
  // Handle config toggle changes
  const handleConfigChange = (key) => {
    const newConfig = {
      ...reportConfig,
      [key]: !reportConfig[key]
    };
    setReportConfig(newConfig);
    dispatch(updateReportConfig(newConfig));
  };
  
  // Handle report name change
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setReportName(newName);
    dispatch(updateReportName(newName));
  };
  
  // Generate report
  const handleGenerateReport = () => {
    if (!currentFile?.fileID) {
      alert('No file selected. Please select a file to generate a report.');
      return;
    }
    
    dispatch(generateReport({
      fileID: currentFile.fileID,
      name: reportName,
      config: reportConfig
    }));
  };
  
  // Export as PDF
  const handleExportPdf = () => {
    if (!reportData?.reportID) {
      alert('No report generated yet. Please generate a report first.');
      return;
    }
    
    dispatch(exportReportPdf({
      reportID: reportData.reportID,
      config: reportConfig
    }));
  };
  
  // Export as Excel
  const handleExportExcel = () => {
    if (!reportData?.reportID) {
      alert('No report generated yet. Please generate a report first.');
      return;
    }
    
    dispatch(exportReportExcel({
      reportID: reportData.reportID
    }));
  };
  
  // Helper to format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };
  
  // Helper to get top parts by savings potential
  const getTopParts = () => {
    if (!valueSavingsData?.perSku) return [];
    
    // Sort parts by total annual savings and take the top 5
    return [...valueSavingsData.perSku]
      .sort((a, b) => (b.totalAnnualSavings || 0) - (a.totalAnnualSavings || 0))
      .slice(0, 5)
      .map(part => ({
        originalName: part.name,
        matchedCategory: part.category,
        savings: part.totalAnnualSavings
      }));
  };
  
  // Helper to get top categories by savings
  const getTopCategories = () => {
    if (!valueSavingsData?.perSku) return [];
    
    // Group parts by category and sum savings
    const categorySavings = valueSavingsData.perSku.reduce((acc, part) => {
      const category = part.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = 0;
      }
      acc[category] += (part.totalAnnualSavings || 0);
      return acc;
    }, {});
    
    // Convert to array, sort by savings, and take top 5
    return Object.entries(categorySavings)
      .map(([name, savings]) => ({ name, savings }))
      .sort((a, b) => b.savings - a.savings)
      .slice(0, 5);
  };
  
  // Check if we have enough data to render the preview
  const hasRequiredData = calculationResults && calculationResults.parts && calculationResults.parts.length > 0 && 
                         valueSavingsData && valueSavingsData.summary;
  
  // Check if a report is being generated
  const isGenerating = reportData?.generating;
  
  // Check if we have a generated report
  const hasReport = reportData?.reportID;
  
  // Render report preview based on calculation and value savings data
  const renderReportPreview = () => {
    if (!hasRequiredData) {
      return (
        <div className="no-data">
          No calculation or value savings data available for preview. 
          {currentFile?.fileID && (
            <div className="data-fetch-notice">
              <p>Trying to fetch the necessary data...</p>
              <button 
                className="secondary-button" 
                onClick={() => {
                  // Retry fetching data
                  dispatch(getCalculationResults(currentFile.fileID));
                  dispatch(getValueSavingsAnalysis(currentFile.fileID));
                }}
              >
                Retry Fetching Data
              </button>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="report-preview">
        {reportConfig.includeExecutiveSummary && (
          <div className="preview-section">
            <h3>Executive Summary</h3>
            <p>This analysis evaluated {calculationResults.parts?.length || 0} parts for potential savings through on-demand manufacturing.</p>
            {valueSavingsData?.summary && (
              <div className="summary-metrics">
                <div className="metric">
                  <span className="metric-value">{formatCurrency(valueSavingsData.summary.totalAnnualSavings)}</span>
                  <span className="metric-label">Total Annual Savings</span>
                </div>
                <div className="metric">
                  <span className="metric-value">{formatCurrency(valueSavingsData.summary.averageSavingsPerSku)}</span>
                  <span className="metric-label">Avg. Savings Per SKU</span>
                </div>
              </div>
            )}
          </div>
        )}
        
        {reportConfig.includeTopParts && calculationResults.parts && (
          <div className="preview-section">
            <h3>Top Parts by Savings Potential</h3>
            <div className="top-parts">
              <table>
                <thead>
                  <tr>
                    <th>Part Name</th>
                    <th>Category</th>
                    <th>Potential Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {getTopParts().map((part, index) => (
                    <tr key={index}>
                      <td>{part.originalName}</td>
                      <td>{part.matchedCategory}</td>
                      <td>{formatCurrency(part.savings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {reportConfig.includeTopCategories && (
          <div className="preview-section">
            <h3>Top Categories by Savings</h3>
            <div className="top-categories">
              {getTopCategories().map((category, index) => (
                <div key={index} className="category-item">
                  <span className="category-name">{category.name}</span>
                  <span className="category-value">{formatCurrency(category.savings)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {reportConfig.includeRecommendations && (
          <div className="preview-section">
            <h3>Recommendations</h3>
            <ul className="recommendations-list">
              <li>Focus on high-savings parts for immediate cost reduction</li>
              <li>Implement on-demand manufacturing for parts with longest lead times</li>
              <li>Reduce inventory costs by transitioning to just-in-time production</li>
              <li>Consider consolidating suppliers for common part categories</li>
            </ul>
          </div>
        )}
        
        {reportConfig.includeSavingsBreakdown && (
          <div className="preview-section">
            <h3>Savings Breakdown</h3>
            <p>The detailed breakdown of savings is available in the Excel export.</p>
            <p>Key Highlights:</p>
            <ul>
              <li>Inventory Holding Cost Savings: {valueSavingsData?.summary ? formatCurrency(valueSavingsData.summary.totalInventoryHoldingCostSavings) : 'N/A'}</li>
              <li>Obsolescence Savings: {valueSavingsData?.summary ? formatCurrency(valueSavingsData.summary.totalObsolescenceSavings) : 'N/A'}</li>
              <li>Safety Stock Savings: {valueSavingsData?.summary ? formatCurrency(valueSavingsData.summary.totalSafetyStockSavings) : 'N/A'}</li>
            </ul>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="report-generation-section">
      <h2>Report Generation</h2>
      
      {reportData?.error && (
        <div className="error-message">
          {reportData.error}
        </div>
      )}
      
      <div className="report-config-card">
        <h3>Report Configuration</h3>
        
        <div className="report-name-field">
          <label htmlFor="reportName">Report Name:</label>
          <input
            id="reportName"
            type="text"
            value={reportName}
            onChange={handleNameChange}
            className="report-name-input"
            placeholder="Enter report name"
          />
        </div>
        
        <div className="config-toggles">
          <div className="config-item">
            <label>
              <input
                type="checkbox"
                checked={reportConfig.includeExecutiveSummary}
                onChange={() => handleConfigChange('includeExecutiveSummary')}
              />
              Include Executive Summary
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <input
                type="checkbox"
                checked={reportConfig.includeTopParts}
                onChange={() => handleConfigChange('includeTopParts')}
              />
              Include Top Parts
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <input
                type="checkbox"
                checked={reportConfig.includeTopCategories}
                onChange={() => handleConfigChange('includeTopCategories')}
              />
              Include Top Categories
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <input
                type="checkbox"
                checked={reportConfig.includeRecommendations}
                onChange={() => handleConfigChange('includeRecommendations')}
              />
              Include Recommendations
            </label>
          </div>
          
          <div className="config-item">
            <label>
              <input
                type="checkbox"
                checked={reportConfig.includeSavingsBreakdown}
                onChange={() => handleConfigChange('includeSavingsBreakdown')}
              />
              Include Savings Breakdown
            </label>
          </div>
        </div>
        
        <div className="generate-button-container">
          <button
            className="primary-button"
            onClick={handleGenerateReport}
            disabled={!currentFile?.fileID || isGenerating}
          >
            {isGenerating ? 'Generating...' : hasReport ? 'Regenerate Report' : 'Generate Report'}
          </button>
        </div>
      </div>
      
      <div className="report-preview-card">
        <div className="preview-header">
          <h3>Report Preview</h3>
          {hasReport && (
            <div className="export-buttons">
              <button
                className="export-button pdf-button"
                onClick={handleExportPdf}
                disabled={reportData?.exporting}
              >
                Download PDF
              </button>
              <button
                className="export-button excel-button"
                onClick={handleExportExcel}
                disabled={reportData?.exporting}
              >
                Download Excel
              </button>
            </div>
          )}
        </div>
        
        {isGenerating ? (
          <div className="generating-indicator">
            <p>Generating report...</p>
            <div className="progress-spinner"></div>
          </div>
        ) : (
          renderReportPreview()
        )}
      </div>
    </div>
  );
};

export default ReportGeneration;